// Решетката на работния лист „Изписване" (от десктоп ширина нагоре): плоски
// клетки като в доставката (прозрачни полета, оранжева рамка на фокус, id
// `wox-{ключ на реда}-{колона}`, стрелковата навигация е на страницата) —
// продуктови редове (наличност към датата, партида/FIFO, ед. цена, Нето/Бруто,
// бележка) и редове-рецепти (порции, стойността по съставките, лентата „Не
// стига"; разделяне на продукти / връщане в рецепта — WriteoffRecipeRow). Под
// десктоп ширина редовете са карти (WriteoffLineCards) със СЪЩИТЕ id-та.
// Логиката на редовете е в `useWriteoffRowsActions`.
import { Fragment } from "react";
import { Select } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { Product, StockAsOf } from "../../../../api/inventory";
import { KbdChip } from "../../components/KbdChip";
import { TruncatedText } from "../../components/list/TruncatedText";
import {
  SHEET_CONTROL_HEIGHT,
  SHEET_HEADER_CELL,
  SHEET_HEADER_ROW,
  SHEET_META_TEXT,
  SHEET_NAME_CELL,
  SHEET_NAME_TEXT,
  SHEET_ROW,
} from "../../components/list/density";
import { PickCell } from "../../components/PickCell";
import { RecipeGroupHeader, WriteoffLineCards } from "./WriteoffLineCards";
import { WriteoffTotalsRow } from "./WriteoffTotalsRow";
import { WriteoffRecipeRow } from "./WriteoffRecipeRow";
import { ProductAvailabilityText } from "./WriteoffStockTexts";
import type { RecipeRowStock } from "./useWriteoffStock";
import { shortageByProductOf, type StockShortage } from "./writeoffStockCheck";
import {
  MoneyCell,
  NOTE_INPUT_CLASS,
  PICK_CELL_CLASS,
  PickBox,
  QTY_INPUT_CLASS,
  RemoveRowButton,
  RowCell,
  RowIndexCell,
} from "./WriteoffGridCells";
import {
  LINE_GRID_CLASS,
  LINE_GRID_MIN_WIDTH_CLASS,
  cellId,
  formatQtyInput,
  lotLabelOf,
  parseDecimal,
  productLineValues,
  type WriteoffLineDraft,
} from "./writeoffWorksheetShared";

export type WriteoffTableProps = {
  lines: WriteoffLineDraft[];
  productById: Map<number, Product>;
  asOfByProduct: Map<number, StockAsOf>;
  // продуктите, които не стигат КЪМ ДАТАТА (нуждата — за целия документ)
  shortages: StockShortage[];
  // какво знае листът за всеки ред-рецепта (стойност, недостиг, порции)
  recipeStockByKey: Map<number, RecipeRowStock>;
  vatRegistered: boolean;
  isDesktop: boolean;
  onPatchLine: (key: number, patch: Partial<WriteoffLineDraft>) => void;
  onRequestRemove: (key: number) => void;
  onSplitRecipe: (key: number) => void;
  onFitRecipePortions: (key: number, portions: number) => void;
  onRequestRevert: (groupId: number) => void;
  // ←/→ от отворения избор на партида → съседната клетка
  onSideExit: (key: number, col: number, step: -1 | 1) => void;
  // сумите за реда „Общо" в дъното на решетката
  totalNet: number;
  totalGross: number;
};

export const WriteoffWorksheetTable = (props: WriteoffTableProps) => {
  const { t } = useTranslation();
  const {
    lines,
    productById,
    asOfByProduct,
    shortages,
    recipeStockByKey,
    vatRegistered,
    isDesktop,
    onPatchLine,
    onRequestRemove,
    onSplitRecipe,
    onFitRecipePortions,
    onRequestRevert,
    onSideExit,
    totalNet,
    totalGross,
  } = props;

  // Под десктоп ширина — карти (същите id-та на клетките)
  if (!isDesktop) return <WriteoffLineCards {...props} />;

  const shortageByProduct = shortageByProductOf(shortages);
  const headCell = (label: string, align?: "center" | "right") => (
    <div
      className={`${SHEET_HEADER_CELL} border-r border-shade-2 last:border-r-0 ${
        align === "center" ? "justify-center text-center" : align === "right" ? "justify-end text-right" : "justify-start text-left"
      }`}
    >
      <span className="truncate">{label}</span>
    </div>
  );

  return (
    <div className={`${LINE_GRID_MIN_WIDTH_CLASS} flex min-h-full flex-col`}>
      {/* Заглавен ред (sticky) */}
      <div
        className={`${LINE_GRID_CLASS} ${SHEET_HEADER_ROW} sticky top-0 z-[5] border-b border-shade-3 bg-fill`}
      >
        {headCell("#", "center")}
        {headCell(t("inventoryPage.colProductName"))}
        {headCell(t("inventoryPage.colQty"), "right")}
        {headCell(t("inventoryPage.colBatch"))}
        {headCell(t("inventoryPage.colUnitPrice"), "right")}
        {headCell(t("inventoryPage.colNet"), "right")}
        {headCell(t("inventoryPage.colGross"), "right")}
        {headCell(t("inventoryPage.colNote"))}
        <div />
      </div>

      {lines.map((line, index) => {
        const previous = lines[index - 1];
        const showGroupHeader =
          line.groupId != null && (!previous || previous.groupId !== line.groupId);
        return (
          <Fragment key={line.key}>
            {showGroupHeader && <RecipeGroupHeader line={line} onRequestRevert={onRequestRevert} />}
            {line.kind === "recipe" ? (
              <WriteoffRecipeRow
                line={line}
                index={index}
                stock={recipeStockByKey.get(line.key)}
                onPatchLine={onPatchLine}
                onRequestRemove={onRequestRemove}
                onSplitRecipe={onSplitRecipe}
                onFitPortions={onFitRecipePortions}
              />
            ) : (
              <ProductRow
                line={line}
                index={index}
                product={line.productId != null ? (productById.get(line.productId) ?? null) : null}
                asOf={line.productId != null ? (asOfByProduct.get(line.productId) ?? null) : null}
                shortage={line.productId != null ? shortageByProduct.get(line.productId) : undefined}
                vatRegistered={vatRegistered}
                onSideExit={onSideExit}
                onPatchLine={onPatchLine}
                onRequestRemove={onRequestRemove}
              />
            )}
          </Fragment>
        );
      })}

      {lines.length === 0 && (
        <div className="flex flex-col items-center gap-2.5 px-6 py-16 text-center">
          <div className="flex h-[46px] w-[46px] items-center justify-center rounded-[13px] bg-brand-tint">
            <SearchOutlined className="text-xl text-brand-deep-ink" />
          </div>
          <div className="text-[15px] font-bold text-ink">{t("inventoryPage.woEmptyTitle")}</div>
          <div className="max-w-[480px] text-[12.5px] leading-relaxed text-muted">
            {t("inventoryPage.woEmptyHint")}
          </div>
          <div className="mt-1 flex flex-wrap items-center justify-center gap-1.5 text-xs text-muted">
            <KbdChip>А–Я</KbdChip> {t("inventoryPage.wsEmptyKbdType")}
            <span className="text-ghost">·</span>
            <KbdChip>↵</KbdChip> {t("inventoryPage.wsEmptyKbdEnter")}
            <span className="text-ghost">·</span>
            <KbdChip>Esc</KbdChip> {t("inventoryPage.wsEmptyKbdEsc")}
          </div>
        </div>
      )}

      {lines.length > 0 && <WriteoffTotalsRow count={lines.length} totalNet={totalNet} totalGross={totalGross} />}
    </div>
  );
};

// ─── Продуктов ред ──────────────────────────────────────────────────────────
function ProductRow({
  line,
  index,
  product,
  asOf,
  shortage,
  vatRegistered,
  onSideExit,
  onPatchLine,
  onRequestRemove,
}: {
  line: WriteoffLineDraft;
  index: number;
  product: Product | null;
  asOf: StockAsOf | null;
  shortage: StockShortage | undefined;
  vatRegistered: boolean;
  onSideExit: (key: number, col: number, step: -1 | 1) => void;
  onPatchLine: (key: number, patch: Partial<WriteoffLineDraft>) => void;
  onRequestRemove: (key: number) => void;
}) {
  const { t } = useTranslation();
  const { decimals, unit, available, lots, quantity, unitPrice, netSum, grossSum } =
    productLineValues(line, product, asOf, vatRegistered);
  const lotOptions = [
    { value: "fifo", label: t("inventoryPage.fifo") },
    ...lots.map((lot) => ({ value: String(lot.id), label: lotLabelOf(lot, decimals, unit, t) })),
  ];
  const selectedLot = line.lotId ? (lots.find((lot) => lot.id === line.lotId) ?? null) : null;
  const filled = quantity > 0;
  const grouped = line.groupId != null;
  const over = shortage != null;

  return (
    <div
      className={`${LINE_GRID_CLASS} ${SHEET_ROW} items-stretch border-b border-shade-2 ${
        grouped ? "border-l-[3px] border-l-brand" : ""
      } ${filled ? "bg-panel" : "bg-surface"}`}
    >
      <RowIndexCell index={index} />
      {/* ПРОДУКТ: име + наличност към датата (клампната до 0) */}
      <div className={`${SHEET_NAME_CELL} border-r border-shade-2`}>
        <TruncatedText text={product?.name ?? "—"} className={SHEET_NAME_TEXT} />
        <span className={`${SHEET_META_TEXT} ${over ? "font-bold text-danger-ink" : "text-muted"}`}>
          <ProductAvailabilityText
            line={line}
            available={available}
            decimals={decimals}
            unit={unit}
            shortage={shortage}
          />
        </span>
      </div>
      {/* КОЛИЧЕСТВО — плоска клетка (приема точка и запетая; формат на blur) */}
      <RowCell>
        <input
          id={cellId(line.key, 0)}
          data-rk={line.key}
          data-c={0}
          value={line.quantity}
          placeholder="0"
          inputMode="decimal"
          aria-invalid={over || undefined}
          onChange={(e) => onPatchLine(line.key, { quantity: e.target.value.replace(/[^0-9.,]/g, "") })}
          onFocus={(e) => e.target.select()}
          onBlur={() => {
            const parsed = parseDecimal(line.quantity);
            if (line.quantity !== "" && parsed > 0)
              onPatchLine(line.key, { quantity: formatQtyInput(parsed, decimals) });
          }}
          className={`${QTY_INPUT_CLASS} ${over ? "!border-danger" : ""}`}
        />
      </RowCell>
      {/* ПАРТИДА — общият PickCell: Select се монтира при клик/Enter, а след
          избора фокусът се връща в клетката */}
      <PickBox>
        <PickCell
          id={cellId(line.key, 1)}
          rowKey={line.key}
          col={1}
          className={PICK_CELL_CLASS}
          editorHeight={SHEET_CONTROL_HEIGHT}
          onSideExit={(step) => onSideExit(line.key, 1, step)}
          renderEditor={(close) => (
            <Select
              autoFocus
              defaultOpen
              value={line.lotId ? String(line.lotId) : "fifo"}
              onChange={(v) => {
                onPatchLine(line.key, { lotId: v === "fifo" ? null : Number(v) });
                close();
              }}
              onBlur={() => close(false)}
              popupMatchSelectWidth={false}
              options={lotOptions}
              className="w-full"
            />
          )}
        >
          <span className="truncate">
            {selectedLot ? lotLabelOf(selectedLot, decimals, unit, t) : t("inventoryPage.fifo")}
          </span>
        </PickCell>
      </PickBox>
      {/* ЕД. ЦЕНА — само за четене (от партидата/FIFO, претеглена; до 5 знака) */}
      <MoneyCell value={unitPrice} unitPrice />
      <MoneyCell value={netSum} />
      <MoneyCell value={grossSum} strong />
      {/* БЕЛЕЖКА — плоска клетка */}
      <RowCell>
        <input
          id={cellId(line.key, 2)}
          data-rk={line.key}
          data-c={2}
          value={line.note}
          placeholder={t("inventoryPage.notePh")}
          onChange={(e) => onPatchLine(line.key, { note: e.target.value })}
          className={NOTE_INPUT_CLASS}
        />
      </RowCell>
      {/* ✕ — запълва цялата 40px клетка, както в доставката */}
      <RemoveRowButton onClick={() => onRequestRemove(line.key)} hint={t("inventoryPage.confirmDeleteRowTitle")} />
    </div>
  );
}

export default WriteoffWorksheetTable;
